import { useMemo, useState } from "react";
import { Card } from "antd";
import { toast } from "react-hot-toast";
import Footer from "../components/Footer";
import Header from "../components/Header";
import { useTranslation } from "react-i18next";
import StandardButton from "../components/StandardButton";

const ALIGNMENTS = [
  { key: "left", labelKey: "markdown_table_align_left" },
  { key: "center", labelKey: "markdown_table_align_center" },
  { key: "right", labelKey: "markdown_table_align_right" },
];

const MAX_ROWS = 50;
const MAX_COLS = 12;

const IMPORT_DELIMITERS = [
  { char: "\t", labelKey: "markdown_table_import_delim_tab" },
  { char: ",", labelKey: "markdown_table_import_delim_comma" },
  { char: ";", labelKey: "markdown_table_import_delim_semicolon" },
  { char: "|", labelKey: "markdown_table_import_delim_pipe" },
];

const splitLines = (text) => {
  const lines = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          current += '""';
          i += 1;
        } else {
          inQuotes = false;
          current += ch;
        }
      } else {
        current += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
      current += ch;
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && text[i + 1] === "\n") i += 1;
      lines.push(current);
      current = "";
    } else {
      current += ch;
    }
  }
  if (current !== "") lines.push(current);
  return lines.filter((line) => line.trim() !== "");
};

const countDelimOutsideQuotes = (line, delim) => {
  let count = 0;
  let q = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (q) {
      if (ch === '"') {
        if (line[i + 1] === '"') i += 1;
        else q = false;
      }
    } else if (ch === '"') {
      q = true;
    } else if (ch === delim) {
      count += 1;
    }
  }
  return count;
};

const detectImportDelimiter = (text) => {
  const stripped = text.replace(/^\uFEFF/, "");
  const lines = splitLines(stripped).slice(0, 10);
  if (lines.length === 0) return null;
  let best = null;
  IMPORT_DELIMITERS.forEach(({ char }) => {
    const counts = lines.map((l) => countDelimOutsideQuotes(l, char));
    const first = counts[0];
    if (first === 0) return;
    const consistent = counts.every((c) => c === first);
    const total = counts.reduce((a, b) => a + b, 0);
    const score = (consistent ? 1000 : 0) + total;
    if (!best || score > best.score) best = { char, score };
  });
  return best ? best.char : null;
};

const parseDelimited = (text, delimiter) => {
  const rows = [];
  let field = "";
  let row = [];
  let inQuotes = false;
  const stripped = text.replace(/^\uFEFF/, "");
  for (let i = 0; i < stripped.length; i += 1) {
    const ch = stripped[i];
    if (inQuotes) {
      if (ch === '"') {
        if (stripped[i + 1] === '"') {
          field += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
    } else if (ch === '"' && field === "") {
      inQuotes = true;
    } else if (ch === delimiter) {
      row.push(field);
      field = "";
    } else if (ch === "\n" || ch === "\r") {
      row.push(field);
      field = "";
      if (ch === "\r" && stripped[i + 1] === "\n") i += 1;
      if (row.length > 1 || row[0] !== "") rows.push(row);
      row = [];
    } else {
      field += ch;
    }
  }
  if (field !== "" || row.length > 0) {
    row.push(field);
    if (row.length > 1 || row[0] !== "") rows.push(row);
  }
  return rows;
};

const DEFAULT_HEADERS = ["Name", "Type", "Default", "Description"];
const DEFAULT_ROWS = [
  ["id", "string", "—", "Unique identifier of the record."],
  ["createdAt", "Date", "now()", "Timestamp the record was created."],
  ["active", "boolean", "true", "Whether the record is enabled."],
];

const escapeCell = (value) => {
  if (value == null) return "";
  return String(value).replace(/\|/g, "\\|").replace(/\r?\n/g, " ");
};

const padCell = (value, width, alignment) => {
  const text = value ?? "";
  const diff = Math.max(0, width - text.length);
  if (alignment === "right") return " ".repeat(diff) + text;
  if (alignment === "center") {
    const left = Math.floor(diff / 2);
    const right = diff - left;
    return " ".repeat(left) + text + " ".repeat(right);
  }
  return text + " ".repeat(diff);
};

const buildAlignmentMarker = (width, alignment) => {
  const w = Math.max(width, 3);
  if (alignment === "center") return ":" + "-".repeat(w - 2) + ":";
  if (alignment === "right") return "-".repeat(w - 1) + ":";
  if (alignment === "left") return ":" + "-".repeat(w - 1);
  return "-".repeat(w);
};

const buildTable = (headers, rows, alignments, { compact, prettyAlign }) => {
  if (headers.length === 0) return "";
  const safeHeaders = headers.map(escapeCell);
  const safeRows = rows.map((row) =>
    headers.map((_, c) => escapeCell(row[c] ?? "")),
  );

  if (compact) {
    const headerLine = `| ${safeHeaders.join(" | ")} |`;
    const sepLine = `| ${headers
      .map((_, c) => buildAlignmentMarker(3, alignments[c] || "left"))
      .join(" | ")} |`;
    const bodyLines = safeRows.map((row) => `| ${row.join(" | ")} |`);
    return [headerLine, sepLine, ...bodyLines].join("\n");
  }

  const widths = headers.map((_, c) => {
    const cellLengths = [safeHeaders[c].length, ...safeRows.map((r) => r[c].length)];
    const widest = Math.max(3, ...cellLengths);
    return prettyAlign ? widest : Math.max(3, safeHeaders[c].length);
  });

  const headerLine = `| ${safeHeaders
    .map((h, c) => padCell(h, widths[c], alignments[c] || "left"))
    .join(" | ")} |`;
  const sepLine = `| ${headers
    .map((_, c) => buildAlignmentMarker(widths[c], alignments[c] || "left"))
    .join(" | ")} |`;
  const bodyLines = safeRows.map(
    (row) =>
      `| ${row
        .map((cell, c) => padCell(cell, widths[c], alignments[c] || "left"))
        .join(" | ")} |`,
  );

  return [headerLine, sepLine, ...bodyLines].join("\n");
};

const MarkdownTableGenerator = () => {
  const { t } = useTranslation();
  const [headers, setHeaders] = useState(DEFAULT_HEADERS);
  const [rows, setRows] = useState(DEFAULT_ROWS);
  const [alignments, setAlignments] = useState(
    DEFAULT_HEADERS.map(() => "left"),
  );
  const [compact, setCompact] = useState(false);
  const [prettyAlign, setPrettyAlign] = useState(true);
  const [showImport, setShowImport] = useState(false);
  const [importText, setImportText] = useState("");
  const [importHasHeader, setImportHasHeader] = useState(true);

  const detectedDelimiter = useMemo(
    () => detectImportDelimiter(importText),
    [importText],
  );
  const detectedDelimiterLabel = useMemo(() => {
    const found = IMPORT_DELIMITERS.find(
      (d) => d.char === detectedDelimiter,
    );
    return found ? t(found.labelKey) : null;
  }, [detectedDelimiter, t]);

  const colCount = headers.length;
  const rowCount = rows.length;

  const setHeader = (index, value) => {
    setHeaders((prev) => prev.map((h, i) => (i === index ? value : h)));
  };

  const setAlignment = (index, value) => {
    setAlignments((prev) => prev.map((a, i) => (i === index ? value : a)));
  };

  const setCell = (rowIndex, colIndex, value) => {
    setRows((prev) =>
      prev.map((row, r) =>
        r === rowIndex
          ? row.map((cell, c) => (c === colIndex ? value : cell))
          : row,
      ),
    );
  };

  const addColumn = () => {
    if (colCount >= MAX_COLS) return;
    setHeaders((prev) => [...prev, `Column ${prev.length + 1}`]);
    setAlignments((prev) => [...prev, "left"]);
    setRows((prev) => prev.map((row) => [...row, ""]));
  };

  const removeColumn = (index) => {
    if (colCount <= 1) return;
    setHeaders((prev) => prev.filter((_, i) => i !== index));
    setAlignments((prev) => prev.filter((_, i) => i !== index));
    setRows((prev) => prev.map((row) => row.filter((_, i) => i !== index)));
  };

  const addRow = () => {
    if (rowCount >= MAX_ROWS) return;
    setRows((prev) => [...prev, Array(colCount).fill("")]);
  };

  const removeRow = (index) => {
    if (rowCount <= 1) return;
    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  const reset = () => {
    setHeaders(DEFAULT_HEADERS);
    setRows(DEFAULT_ROWS);
    setAlignments(DEFAULT_HEADERS.map(() => "left"));
  };

  const clear = () => {
    setHeaders(["Column 1", "Column 2"]);
    setRows([["", ""]]);
    setAlignments(["left", "left"]);
  };

  const applyImport = () => {
    if (!importText.trim()) {
      toast.error(t("markdown_table_import_empty"));
      return;
    }
    const delimiter = detectedDelimiter || "\t";
    const parsed = parseDelimited(importText, delimiter);
    if (parsed.length === 0) {
      toast.error(t("markdown_table_import_empty"));
      return;
    }
    const maxCols = parsed.reduce((m, r) => Math.max(m, r.length), 0);
    const colCountRaw = Math.max(1, maxCols);
    const colCountNew = Math.min(colCountRaw, MAX_COLS);
    const normalized = parsed.map((r) => {
      const padded = r.slice(0, colCountNew);
      while (padded.length < colCountNew) padded.push("");
      return padded;
    });
    let headerRow;
    let bodyRows;
    if (importHasHeader && normalized.length > 0) {
      headerRow = normalized[0].map((h, i) => h.trim() || `Column ${i + 1}`);
      bodyRows = normalized.slice(1);
    } else {
      headerRow = Array.from(
        { length: colCountNew },
        (_, i) => `Column ${i + 1}`,
      );
      bodyRows = normalized;
    }
    const bodyClamped = bodyRows.slice(0, MAX_ROWS);
    const finalRows =
      bodyClamped.length > 0
        ? bodyClamped
        : [Array(colCountNew).fill("")];
    const clamped =
      colCountRaw > MAX_COLS || bodyRows.length > MAX_ROWS;
    setHeaders(headerRow);
    setRows(finalRows);
    setAlignments(headerRow.map(() => "left"));
    setShowImport(false);
    setImportText("");
    if (clamped) {
      toast(
        t("markdown_table_import_clamped", {
          maxCols: MAX_COLS,
          maxRows: MAX_ROWS,
        }),
        { icon: "⚠️" },
      );
    } else {
      toast.success(
        t("markdown_table_import_success", {
          cols: headerRow.length,
          rows: finalRows.length,
        }),
      );
    }
  };

  const cancelImport = () => {
    setShowImport(false);
    setImportText("");
  };

  const output = useMemo(
    () => buildTable(headers, rows, alignments, { compact, prettyAlign }),
    [headers, rows, alignments, compact, prettyAlign],
  );

  const copy = async () => {
    if (!output) {
      toast.error(t("markdown_table_nothing_to_copy"));
      return;
    }
    try {
      await navigator.clipboard.writeText(output);
      toast.success(t("copy_success"));
    } catch {
      toast.error(t("failed_to_copy"));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-indigo-50 to-white p-0 text-gray-800">
      <Header />
      <div className="max-w-5xl mx-auto p-4">
        <Card className="shadow-lg rounded-xl">
          <div className="p-4 flex flex-col gap-4">
            <h1 className="text-2xl font-bold text-indigo-700">
              {t("markdown_table_title")}
            </h1>
            <p className="text-gray-600">{t("markdown_table_intro")}</p>

            <div className="bg-indigo-50 p-4 rounded-md border border-indigo-200 flex flex-col gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <StandardButton
                  onClick={addColumn}
                  type="primary"
                  size="small"
                  disabled={colCount >= MAX_COLS}
                >
                  {t("markdown_table_add_column")}
                </StandardButton>
                <StandardButton
                  onClick={addRow}
                  type="primary"
                  size="small"
                  disabled={rowCount >= MAX_ROWS}
                >
                  {t("markdown_table_add_row")}
                </StandardButton>
                <StandardButton
                  onClick={reset}
                  type="secondary"
                  variant="outlined"
                  size="small"
                >
                  {t("markdown_table_reset")}
                </StandardButton>
                <StandardButton
                  onClick={clear}
                  type="secondary"
                  variant="outlined"
                  size="small"
                >
                  {t("markdown_table_clear")}
                </StandardButton>
                <StandardButton
                  onClick={() => setShowImport((v) => !v)}
                  type="secondary"
                  variant="outlined"
                  size="small"
                  aria-expanded={showImport}
                >
                  {t("markdown_table_import")}
                </StandardButton>
              </div>

              {showImport && (
                <div className="bg-white p-3 rounded-md border border-indigo-200 flex flex-col gap-2">
                  <p className="text-sm text-gray-700">
                    {t("markdown_table_import_help")}
                  </p>
                  <textarea
                    rows={5}
                    className="w-full p-2 font-mono text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                    placeholder={t("markdown_table_import_placeholder")}
                    value={importText}
                    onChange={(e) => setImportText(e.target.value)}
                    aria-label={t("markdown_table_import")}
                  />
                  <div className="flex flex-wrap items-center gap-3">
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={importHasHeader}
                        onChange={(e) =>
                          setImportHasHeader(e.target.checked)
                        }
                        className="h-4 w-4"
                      />
                      {t("markdown_table_import_has_header")}
                    </label>
                    {detectedDelimiterLabel && (
                      <span className="text-xs text-gray-500">
                        {t("markdown_table_import_detected", {
                          delimiter: detectedDelimiterLabel,
                        })}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <StandardButton
                      onClick={applyImport}
                      type="primary"
                      size="small"
                      disabled={!importText.trim()}
                    >
                      {t("markdown_table_import_apply")}
                    </StandardButton>
                    <StandardButton
                      onClick={cancelImport}
                      type="secondary"
                      variant="outlined"
                      size="small"
                    >
                      {t("markdown_table_import_cancel")}
                    </StandardButton>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={prettyAlign}
                    onChange={(e) => setPrettyAlign(e.target.checked)}
                    className="h-4 w-4"
                  />
                  {t("markdown_table_pretty_align")}
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={compact}
                    onChange={(e) => setCompact(e.target.checked)}
                    className="h-4 w-4"
                  />
                  {t("markdown_table_compact")}
                </label>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr>
                    {headers.map((header, c) => (
                      <th
                        key={`th-${c}`}
                        className="border border-gray-200 p-2 align-top bg-gray-50"
                      >
                        <div className="flex flex-col gap-1">
                          <input
                            type="text"
                            value={header}
                            onChange={(e) => setHeader(c, e.target.value)}
                            placeholder={t(
                              "markdown_table_header_placeholder",
                              { index: c + 1 },
                            )}
                            className="w-full p-1 font-mono border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                          />
                          <select
                            value={alignments[c] || "left"}
                            onChange={(e) => setAlignment(c, e.target.value)}
                            className="w-full p-1 text-xs border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                          >
                            {ALIGNMENTS.map((a) => (
                              <option key={a.key} value={a.key}>
                                {t(a.labelKey)}
                              </option>
                            ))}
                          </select>
                          <StandardButton
                            onClick={() => removeColumn(c)}
                            type="secondary"
                            variant="outlined"
                            size="small"
                            disabled={colCount <= 1}
                          >
                            {t("markdown_table_remove_column")}
                          </StandardButton>
                        </div>
                      </th>
                    ))}
                    <th className="w-10 border border-gray-200 bg-gray-50" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, r) => (
                    <tr key={`tr-${r}`}>
                      {row.map((cell, c) => (
                        <td
                          key={`td-${r}-${c}`}
                          className="border border-gray-200 p-1 align-top"
                        >
                          <input
                            type="text"
                            value={cell}
                            onChange={(e) => setCell(r, c, e.target.value)}
                            placeholder={t(
                              "markdown_table_cell_placeholder",
                              { row: r + 1, col: c + 1 },
                            )}
                            className="w-full p-1 font-mono border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                          />
                        </td>
                      ))}
                      <td className="border border-gray-200 p-1 align-top text-center">
                        <StandardButton
                          onClick={() => removeRow(r)}
                          type="secondary"
                          variant="outlined"
                          size="small"
                          disabled={rowCount <= 1}
                        >
                          ×
                        </StandardButton>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">
                  {t("markdown_table_output_label")}
                </label>
                <div className="flex items-center gap-2">
                  <StandardButton
                    onClick={copy}
                    type="primary"
                    size="small"
                    disabled={!output}
                  >
                    {t("copy_button")}
                  </StandardButton>
                </div>
              </div>
              <pre className="w-full min-h-[6rem] p-2 font-mono text-sm bg-gray-50 border border-gray-200 rounded-md whitespace-pre overflow-x-auto text-gray-800">
                {output || (
                  <em className="text-gray-400 not-italic">
                    {t("markdown_table_output_empty")}
                  </em>
                )}
              </pre>
              <div className="text-xs text-gray-500">
                {t("markdown_table_stats", {
                  rows: rowCount,
                  cols: colCount,
                })}
              </div>
            </div>

            <div className="text-xs text-gray-500 leading-relaxed">
              <p>{t("markdown_table_note_pipes")}</p>
              <p>{t("markdown_table_note_alignment")}</p>
              <p>{t("markdown_table_note_privacy")}</p>
            </div>
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default MarkdownTableGenerator;
